import { useState, useEffect, type ReactNode } from 'react';
import { CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { NATIVE_FIELD_CLASS } from '@/contabilidad/constants/ui';
import { formatAmount } from '@/contabilidad/utils/formatters';
import { NumberInput } from '@/components/ui/NumberInput';
import { FormField } from './FormField';
import { NativeSelect } from './NativeSelect';
import { DateInput } from './DateInput';
import { cn } from '@/lib/utils';
import type { Moneda, CuentaBancaria, PagoPayload, ItemPresupuestoResumen } from '@/contabilidad/types';

interface Props {
  open: boolean;
  titulo: string;
  cajaLabel: string;
  itemNombre: string;
  clienteNombre: string;
  total: number;
  totalMovido: number;
  saldoRestante: number;
  moneda: Moneda;
  cajas: CuentaBancaria[];
  isSubmitting?: boolean;
  onSubmit: (payload: PagoPayload) => Promise<void>;
  onClose: () => void;
  /** Desglose informativo de ítems (ingreso/egreso) que componen el total. Solo escrituras. */
  items?: ItemPresupuestoResumen[];
  /** Permite registrar un monto mayor al saldo; el excedente deja el saldo en negativo. */
  permitirExcedente?: boolean;
  /** Motivo por el que solo se puede registrar el saldo completo; fija el monto. */
  montoBloqueado?: string;
  /** Campos extra antes de la caja, p. ej. para elegir a quién se cobra. */
  children?: ReactNode;
}

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

type EstadoPago = 'pendiente' | 'parcial' | 'completo';

function estadoPago(totalMovido: number, total: number): EstadoPago {
  if (totalMovido <= 0) return 'pendiente';
  if (totalMovido >= total) return 'completo';
  return 'parcial';
}

export function RegistrarPagoModal({
  open,
  titulo,
  cajaLabel,
  itemNombre,
  clienteNombre,
  total,
  totalMovido,
  saldoRestante,
  moneda,
  cajas,
  isSubmitting = false,
  onSubmit,
  onClose,
  items,
  permitirExcedente = false,
  montoBloqueado,
  children,
}: Props) {
  const [cajaId, setCajaId] = useState('');
  const [monto, setMonto] = useState('');
  const [fecha, setFecha] = useState(todayISO());
  const [descripcion, setDescripcion] = useState('');
  const [errors, setErrors] = useState<{ cajaId?: boolean; monto?: boolean }>({});

  const cajasFiltradas = cajas.filter((c) => c.moneda === moneda);
  const porcentaje = total > 0 ? Math.min((totalMovido / total) * 100, 100) : 0;
  const estado = estadoPago(totalMovido, total);
  const montoNum = Number(monto);
  const excedente = permitirExcedente && montoNum > saldoRestante ? montoNum - saldoRestante : 0;

  useEffect(() => {
    if (!open) return;
    setCajaId('');
    setMonto(saldoRestante > 0 ? String(saldoRestante) : '');
    setFecha(todayISO());
    setDescripcion('');
    setErrors({});
  }, [open, saldoRestante]);

  async function handleSubmit() {
    const newErrors = {
      cajaId: !cajaId,
      monto: !monto || montoNum <= 0 || (!permitirExcedente && montoNum > saldoRestante),
    };
    if (Object.values(newErrors).some(Boolean)) {
      setErrors(newErrors);
      return;
    }
    await onSubmit({
      monto: montoNum,
      cajaId: Number(cajaId),
      fecha,
      descripcion: descripcion || undefined,
    });
  }

  const fmt = (v: number) => `${moneda} $ ${formatAmount(v, moneda)}`;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-gray-900">{titulo}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-1">

          {/* Resumen del ítem */}
          <div className="rounded-lg border border-gray-200 bg-slate-50 p-4 space-y-3">
            <div className="space-y-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Ítem</p>
              <p className="text-sm font-semibold text-gray-900">{itemNombre}</p>
              <p className="text-xs text-muted-foreground">{clienteNombre}</p>
            </div>

            {items && items.length > 1 && (
              <ul className="space-y-0.5 border-t border-gray-200 pt-2">
                {items.map((it, idx) => (
                  <li key={idx} className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      {it.nombre}{' '}
                      <span className={it.tipo === 'ingreso' ? 'text-blue-500' : 'text-slate-500'}>
                        ({it.tipo})
                      </span>
                    </span>
                    <span className="tabular-nums text-gray-700">{fmt(it.monto)}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
              <div>
                <p className="text-[11px] text-muted-foreground">Total</p>
                <p className="text-sm font-semibold tabular-nums">{fmt(total)}</p>
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground">Registrado</p>
                <p className="text-sm font-semibold tabular-nums text-primary">{fmt(totalMovido)}</p>
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground">Saldo</p>
                <p className={cn('text-sm font-semibold tabular-nums', saldoRestante > 0 ? 'text-slate-600' : 'text-blue-600')}>
                  {fmt(saldoRestante)}
                </p>
              </div>
            </div>

            <div className="space-y-1.5">
              <Progress value={porcentaje} className={cn('[&>div]:transition-all', estado === 'completo' ? '[&>div]:bg-blue-500' : estado === 'parcial' ? '[&>div]:bg-sky-400' : '[&>div]:bg-slate-400')} />
              <div className="flex items-center gap-1.5">
                {estado === 'completo' && <><CheckCircle2 className="size-3.5 text-blue-600" /><span className="text-xs text-blue-600  font-medium">Cobrado</span></>}
                {estado === 'parcial' && <><AlertCircle className="size-3.5 text-sky-500" /><span className="text-xs text-sky-500   font-medium">Parcial ({Math.round(porcentaje)}%)</span></>}
                {estado === 'pendiente' && <><Clock className="size-3.5 text-slate-500" /><span className="text-xs text-slate-500  font-medium">Pendiente</span></>}
              </div>
            </div>
          </div>

          {/* Formulario */}
          {children}

          <FormField label={cajaLabel} required>
            <NativeSelect
              value={cajaId}
              onChange={(e) => { setCajaId(e.target.value); setErrors((p) => ({ ...p, cajaId: false })); }}
              error={errors.cajaId}
            >
              <option value="">Seleccionar caja...</option>
              {cajasFiltradas.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.nombre} (Saldo: {c.moneda} $ {formatAmount(c.saldo, c.moneda)})
                </option>
              ))}
            </NativeSelect>
          </FormField>

          <div className="flex gap-3">
            <FormField label="Monto a registrar" required className="flex-1">
              <NumberInput
                id="monto"
                decimals={2}
                min={0}
                max={permitirExcedente ? undefined : saldoRestante}
                placeholder={String(saldoRestante)}
                value={monto}
                onChange={(value) => { setMonto(value); setErrors((p) => ({ ...p, monto: false })); }}
                className={cn(NATIVE_FIELD_CLASS, errors.monto && 'border-rose-500 focus:border-rose-500', 'text-right')}
                disabled={saldoRestante <= 0 || !!montoBloqueado}
              />
              {errors.monto && (
                <p className="text-xs text-rose-500 mt-0.5">
                  {permitirExcedente
                    ? 'Ingresá un monto mayor a 0'
                    : `Ingresá un monto entre 0 y ${fmt(saldoRestante)}`}
                </p>
              )}
              {montoBloqueado && (
                <p className="text-xs text-muted-foreground mt-0.5">{montoBloqueado}</p>
              )}
              {!errors.monto && excedente > 0 && (
                <p className="text-xs text-amber-600 mt-0.5">
                  El pago supera el saldo en {fmt(excedente)}. El egreso quedará con saldo {fmt(saldoRestante - montoNum)}.
                </p>
              )}
            </FormField>

            <FormField label="Fecha" required className="w-40">
              <DateInput value={fecha} onChange={(e) => setFecha(e.target.value)} />
            </FormField>
          </div>

          <FormField label="Descripción (opcional)">
            <textarea
              placeholder="Nota sobre el pago..."
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              className={cn(NATIVE_FIELD_CLASS, 'h-auto min-h-[64px] py-2 resize-none')}
            />
          </FormField>

        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting} className="rounded-full">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || saldoRestante <= 0}
            className="rounded-full"
          >
            {isSubmitting ? 'Registrando...' : 'Confirmar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
